/**
 * Project-scoped index of subtasks, keyed by parent task id.
 *
 * Used to decide whether a root task should surface as a "context row" because
 * one of its subtasks matches the active filters.
 *
 * Deliberately carries NO structural filters (sprint / bucket / cycle) and no
 * content filters (search / assignee / status):
 *
 *  - Structural filters are applied to every row server-side, but subtasks
 *    inherit them inconsistently at creation time (`subtask.service.ts` inherits
 *    `sprintId` but never `bucketId`; `task-crud.service.ts` inherits neither),
 *    and nothing cascades them when a parent moves. A bucket-scoped index would
 *    return nothing; a backlog-scoped one would return orphans.
 *  - Content filters stay client-side, so the query key never changes as the
 *    user types. Every `ProjectTasksTable` instance on the page therefore
 *    produces an identical key and React Query dedupes them to one request.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { fetchAllPages } from '@/lib/api/paginate'
import { tasksApi } from '@/lib/api/tasks.api'
import { useOrganizationStore } from '@/store/organization.store'
import { keepPreviousData, useQuery } from '@tanstack/react-query'

import { ModuleTypeEnum, TaskWithAssignments } from '@scrambl/shared'

const SUBTASK_INDEX_PAGE_SIZE = 200

/**
 * Query key prefix for the index. Exported so task mutations can keep it in
 * sync — the cached payload is a bare `TaskWithAssignments[]`, which the shared
 * `createTaskCacheUpdater` already knows how to patch. Without this the index
 * goes stale the moment a subtask is edited (e.g. assign someone, then filter
 * by them: the parent would not surface).
 */
export const PROJECT_SUBTASK_INDEX_QUERY_KEY = 'project-subtask-index'

interface UseProjectSubtaskIndexOptions {
  projectId: string
  /** Master gate — the List/table view only. */
  enabled: boolean
  /** Whether any content filter is currently active. */
  hasActiveFilters: boolean
  /** Mirrors the main query so completed subtasks drop out server-side. */
  excludeCompleted: boolean
}

export interface ProjectSubtaskIndex {
  subtasksByParentId: Map<string, TaskWithAssignments[]>
  isLoading: boolean
  isFetching: boolean
}

export function useProjectSubtaskIndex({
  projectId,
  enabled,
  hasActiveFilters,
  excludeCompleted,
}: UseProjectSubtaskIndexOptions): ProjectSubtaskIndex {
  const { currentOrganization } = useOrganizationStore()

  // Sticky activation: don't pay for the index until the user filters for the
  // first time, but once fetched keep it warm so later filter toggles are
  // instant rather than round-tripping.
  const [hasEverActivated, setHasEverActivated] = useState(false)
  const hasEverActivatedRef = useRef(false)

  useEffect(() => {
    if (enabled && hasActiveFilters && !hasEverActivatedRef.current) {
      hasEverActivatedRef.current = true
      setHasEverActivated(true)
    }
  }, [enabled, hasActiveFilters])

  const isEnabled = enabled && hasEverActivated && !!currentOrganization?.id

  const { data, isLoading, isFetching } = useQuery({
    queryKey: [
      PROJECT_SUBTASK_INDEX_QUERY_KEY,
      projectId,
      `excludeCompleted:${excludeCompleted}`,
    ] as const,
    queryFn: async ({ signal }) =>
      fetchAllPages(
        (page, limit) =>
          tasksApi.getTasks(
            {
              page,
              limit,
              moduleType: ModuleTypeEnum.PROJECT,
              moduleId: projectId,
              excludeCompleted,
              includeSubtasks: true,
              sortBy: 'order',
              sortOrder: 'asc',
              view: 'list',
            },
            { signal },
          ),
        SUBTASK_INDEX_PAGE_SIZE,
      ),
    enabled: isEnabled,
    staleTime: 60_000,
    gcTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
  })

  const subtasksByParentId = useMemo(() => {
    const index = new Map<string, TaskWithAssignments[]>()
    if (!data) return index

    for (const task of data) {
      const parentId = task.parentTaskId
      if (!parentId) continue
      const existing = index.get(parentId)
      if (existing) {
        existing.push(task)
      } else {
        index.set(parentId, [task])
      }
    }

    return index
  }, [data])

  return {
    subtasksByParentId,
    isLoading: isEnabled && isLoading,
    isFetching: isEnabled && isFetching,
  }
}
